// Order money math — shared by apps/api (authoritative totals, invoicing) and
// apps/web (live per-line amounts + margin as a rep edits a document).
//
// WHY IT LIVES HERE
// This logic was briefly duplicated: once in apps/api/src/services/order-totals.ts
// and once in apps/web/src/components/documents/money.ts, kept in sync by a
// comment asking the next person to remember. Adversarial review pointed out the
// parity check that comment claimed existed didn't. Rather than add one, the
// duplication is gone — both sides import these functions, so they cannot drift.
// (Same lesson as the four copy-pasted subtotal loops this whole effort started
// from.) The server remains the AUTHORITY: what we persist and bill always comes
// from the server's call into this module; the client uses it for live display.
//
// NO FLOATING POINT. Money is integer cents end to end; the tax rate is integer
// hundredths of a percent. Floats drift (0.1 + 0.2 !== 0.3) and the error
// compounds across lines, per-piece charges and per-colour rows. The tax path in
// particular is exact: verified against BigInt arithmetic over hundreds of
// millions of (subtotal, rate) pairs with zero divergence.
//
// The spec/gate for this module is apps/api/src/db/qa-order-totals.ts
// (`pnpm --filter @merchd/api run db:qa-order-totals:offline`).

// ─── Parsing / formatting ─────────────────────────────────────────────────

/**
 * A money field → integer CENTS. Accepts the numeric-as-string values Drizzle
 * returns for numeric(12,2). Non-finite/garbage → 0 (never NaN downstream).
 * Sub-cent input is rounded at the boundary so no fractional cent can enter an
 * accumulation.
 */
export function toCents(v: string | number | null | undefined): number {
  if (v == null || v === "") return 0;
  const n = typeof v === "number" ? v : Number(v);
  if (!Number.isFinite(n)) return 0;
  // × 100 can land a hair off (4.98 × 100 = 498.0000000000001); round settles it.
  return Math.round(n * 100);
}

/**
 * A COST field → integer MILLS (thousandths of a dollar).
 *
 * 🔴 WHY THIS EXISTS (CEO + GM Stephanie, 2026-08-10). Suppliers publish net
 * costs with THREE decimals — Digispec BG7 is 1.692 / 1.650 / 1.638 / 1.560 /
 * 1.476 — and `product_quantity_brackets` has always stored numeric(12,3). The
 * leak was everything downstream: the cost columns were numeric(12,2) AND
 * `toCents` rounded 1.692 → 169 before multiplying by quantity, so 5,000 pieces
 * booked $8,450 against a true cost of $8,460.
 *
 * ⚠️ Widening the columns alone moves ZERO money — `Math.round(1.692 * 100)` is
 * 169 either way. The money is only recovered by extending in mills and rounding
 * ONCE at the end. That is the whole point of this function.
 *
 * COST SIDE ONLY. `toCents` is untouched and still governs every revenue, tax
 * and billed-total path, so no client-facing number moves by a cent. CEO
 * 2026-08-10: *"it's mainly for net costs rather than client-facing quotes."*
 * Sub-mill input is rounded here so no fractional mill enters an accumulation.
 */
export function toMills(v: string | number | null | undefined): number {
  if (v == null || v === "") return 0;
  const n = typeof v === "number" ? v : Number(v);
  if (!Number.isFinite(n)) return 0;
  // Same float-hair defence as toCents: 1.692 × 1000 = 1691.9999999999998.
  return Math.round(n * 1000);
}

/**
 * Mills → cents, rounding half-up exactly once. Call this at the END of a cost
 * accumulation, never per unit — rounding per unit is the bug this module was
 * changed to fix.
 */
export function millsToCents(mills: number): number {
  return assertSafeCents(divideRoundHalfUp(mills, 10), "mills → cents");
}

/**
 * The same safe-integer guard as assertSafeCents, at mill scale. Mills overflow
 * 10× sooner than cents, so the cost path gets its own explicit check rather
 * than inheriting the cents one and being wrong by an order of magnitude.
 */
export function assertSafeMills(mills: number, what: string): number {
  if (!Number.isSafeInteger(mills)) {
    throw new Error(
      `order-money: ${what} exceeded safe integer range (${mills}). Refusing to ` +
        `return a silently-corrupted money value.`,
    );
  }
  return mills;
}

/** The money format the API accepts for a SELL field (numeric(12,2); leading `-`
 *  for credits). Character-identical to numericString/variantNumeric in
 *  routes/orders.ts. Client-facing prices stay at two decimals — CEO 2026-08-10. */
export const MONEY_INPUT_RE = /^-?\d+(\.\d{1,2})?$/;

/** The money format the API accepts for a COST field (numeric(12,3)). Three
 *  decimals because that is what suppliers publish; see toMills. Deliberately a
 *  SEPARATE constant from MONEY_INPUT_RE so widening cost can never widen sell. */
export const COST_INPUT_RE = /^-?\d+(\.\d{1,3})?$/;

/**
 * Tidy the ways people actually type money, WITHOUT ever guessing at an
 * ambiguous entry.
 *
 * ".50", "4." and "1,200" are all legitimate; rejecting them outright made a
 * rep's input silently vanish. But an unconditional comma strip is far worse:
 * "4,98" (decimal-comma) would become "498" — a 100x price error that passes
 * validation and persists silently, looking like a plausible price. Adversarial
 * review caught exactly that after an earlier revision of this function shipped
 * it, so the comma strip is scoped by lookahead to genuine THOUSANDS grouping
 * (a comma followed by exactly three digits then a non-digit or end-of-string).
 * A comma followed by 1-2 digits is never a thousands separator, so it survives
 * normalisation, fails MONEY_INPUT_RE, and safely reverts.
 *
 * Returning an unchanged/invalid string is the correct outcome for ambiguous
 * input — the caller reverts the field rather than committing a guess.
 */
export function normalizeMoneyInput(raw: string): string {
  const trimmed = raw.trim();
  // No digits at all ("", "-", ".", "-.") is never a number; never coerce it
  // into a meaningful 0.00.
  if (!/\d/.test(trimmed)) return trimmed;
  // A comma with no digits before it isn't a thousands separator either — it's
  // a stray keystroke. Return unchanged so it fails validation rather than
  // quietly becoming a number (",500" must not read as 500).
  if (/^-?,/.test(trimmed)) return trimmed;
  let s = trimmed.replace(/,(?=\d{3}(?:\D|$))/g, "");
  if (s.startsWith(".")) s = `0${s}`;
  else if (s.startsWith("-.")) s = `-0${s.slice(1)}`;
  if (s.endsWith(".")) s = s.slice(0, -1);
  return s;
}

/** Same idea for quantities — "1,200" is a reasonable thing to type. Only
 *  thousands-grouped commas are stripped, for the same reason as above. */
export function normalizeQuantityInput(raw: string): string {
  const trimmed = raw.trim();
  if (!/\d/.test(trimmed)) return trimmed;
  return trimmed.replace(/,(?=\d{3}(?:\D|$))/g, "");
}

/** True when a raw entry, once normalised, is a value the API will accept. */
export function isValidMoneyInput(raw: string): boolean {
  return MONEY_INPUT_RE.test(normalizeMoneyInput(raw));
}

/** Integer cents → a 2dp string, sign-safe. Never goes via a float. */
export function centsToString(cents: number): string {
  const negative = cents < 0;
  const abs = Math.abs(Math.trunc(cents));
  const s = `${Math.floor(abs / 100)}.${String(abs % 100).padStart(2, "0")}`;
  return negative ? `-${s}` : s;
}

/** Integer cents → "$1,234.56" for display. */
export function formatCents(cents: number, currency = "USD"): string {
  const negative = cents < 0;
  const abs = Math.abs(Math.trunc(cents));
  const dollars = Math.floor(abs / 100).toLocaleString();
  const rest = String(abs % 100).padStart(2, "0");
  const symbol = currency === "USD" ? "$" : `${currency} `;
  return `${negative ? "-" : ""}${symbol}${dollars}.${rest}`;
}

/** Format a raw numeric-string money field straight for display. */
export function formatMoney(
  v: string | number | null | undefined,
  currency = "USD",
): string {
  return formatCents(toCents(v), currency);
}

/**
 * Integer mills → a display string, showing the third decimal ONLY when it is
 * significant: 1.692 → "$1.692", but 4.98 → "$4.98".
 *
 * ⚠️ Deliberately not a blanket 3dp format. Every existing cost in production is
 * two-decimal, and rendering the whole app as "$4.980" overnight would read as a
 * bug to the rep and bury the one number that actually gained a decimal. The
 * third place appears exactly where the supplier published one.
 */
export function formatMills(mills: number, currency = "USD"): string {
  const negative = mills < 0;
  const abs = Math.abs(Math.trunc(mills));
  const symbol = currency === "USD" ? "$" : `${currency} `;
  const dollars = Math.floor(abs / 1000).toLocaleString();
  const cents = String(Math.floor((abs % 1000) / 10)).padStart(2, "0");
  const thou = abs % 10;
  const rest = thou === 0 ? cents : `${cents}${thou}`;
  return `${negative ? "-" : ""}${symbol}${dollars}.${rest}`;
}

/**
 * `formatMills` split into a 2dp base and the OPTIONAL third digit, so a column
 * can reserve a fixed slot for that digit and keep every decimal point on the
 * same x-position.
 *
 * ⚠️ Right-alignment does NOT solve this, and neither does `tabular-nums`:
 * tabular figures make each DIGIT equal width, but "$4.98" and "$1.692" are
 * different LENGTHS, so right-aligning them lands the "." in two different
 * places. In a Cost column sitting beside a Price column that is always 2dp,
 * that reads as a formatting bug to a rep scanning down it — and the products
 * that actually carry a third decimal (Gold Bond, Spector) are ones we quote.
 *
 * `{ base: "$1.69", thousandth: "2" }` for 1692 mills; `thousandth` is "" when
 * the third place is not significant, and the caller renders a fixed-width slot
 * either way.
 */
export function formatMillsParts(
  mills: number,
  currency = "USD",
): { base: string; thousandth: string } {
  const negative = mills < 0;
  const abs = Math.abs(Math.trunc(mills));
  const symbol = currency === "USD" ? "$" : `${currency} `;
  const dollars = Math.floor(abs / 1000).toLocaleString();
  const cents = String(Math.floor((abs % 1000) / 10)).padStart(2, "0");
  const thou = abs % 10;
  return {
    base: `${negative ? "-" : ""}${symbol}${dollars}.${cents}`,
    thousandth: thou === 0 ? "" : String(thou),
  };
}

/** A COST field the UI should accept — three decimals. The cost-side mirror of
 *  isValidMoneyInput, sharing its input normalisation (thousands separators,
 *  ".50", "4.") so the two fields behave identically apart from the extra place. */
export function isValidCostInput(raw: string): boolean {
  return COST_INPUT_RE.test(normalizeMoneyInput(raw));
}

/**
 * The value to SHOW in an editable cost box, given the raw numeric string the
 * driver returned.
 *
 * ⚠️ NOT cosmetic. numeric(12,3) makes Postgres return "5.000" where it used to
 * return "5.00", so without this every cost field in the app silently grew a
 * trailing zero the day the column widened — on thousands of existing rows that
 * gained no precision at all. Reps read a changed number as a changed price.
 *
 * Drops ONLY a non-significant third decimal: "5.000" → "5.00", while "1.692"
 * is returned untouched. Never drops the second decimal ("5.00" stays "5.00"),
 * because a money box showing "5.0" reads as unfinished.
 */
export function costInputValue(v: string | number | null | undefined): string {
  if (v == null || v === "") return "";
  const s = String(v);
  const m = /^(-?\d+\.\d\d)(\d)$/.exec(s);
  return m && m[2] === "0" ? m[1]! : s;
}

// ─── Internal helpers ─────────────────────────────────────────────────────

/**
 * A tax rate percent → HUNDREDTHS OF A PERCENT as an exact integer
 * (8.25% → 825). `orders.default_tax_rate` is numeric(5,2), so two decimals is
 * the column's full precision and this is lossless. Keeping the rate integral
 * is what lets the tax calculation avoid floating point entirely.
 */
export function rateHundredths(v: string | number | null | undefined): number {
  if (v == null) return 0;
  const n = typeof v === "number" ? v : Number(v);
  if (!Number.isFinite(n)) return 0;
  return Math.round(n * 100);
}

/** Half-up (away from zero) integer division — no floating point. */
export function divideRoundHalfUp(numerator: number, divisor: number): number {
  const sign = numerator < 0 ? -1 : 1;
  const abs = Math.abs(numerator);
  return sign * Math.floor((abs + divisor / 2) / divisor);
}

/**
 * Guard against silent precision loss. JS doesn't throw when an integer exceeds
 * MAX_SAFE_INTEGER — it just starts lying. On a money path a loud failure beats
 * a wrong number.
 */
export function assertSafeCents(cents: number, what: string): number {
  if (!Number.isSafeInteger(cents)) {
    throw new Error(
      `order-money: ${what} exceeded safe integer range (${cents}). Refusing to ` +
        `return a silently-corrupted money value.`,
    );
  }
  return cents;
}

// ─── Shapes ───────────────────────────────────────────────────────────────

/** A variant row: `unitPrice` null/"" means "no explicit price — use the line's". */
export interface MoneyVariant {
  quantity: number;
  unitPrice: string | number | null;
  /** Per-colour COST. Absent/null falls back to the line cost — a rep can
   *  negotiate a different cost per colour with the supplier. */
  unitCost?: string | number | null;
}

/** A decoration charge. `per_piece` multiplies by the line's total quantity. */
export interface MoneyCharge {
  amount: string | number;
  billingModel: "fixed" | "per_piece";
  /** Rep-facing COST of the charge (CEO 2026-07-24). Optional: legacy callers
   *  and every existing totals path pass none — `amount` (the sell) alone drives
   *  totals. When present it powers charge-inclusive margin (lineChargesCostCents
   *  / lineMarginPctWithCharges / lineAllInCostCents).
   *
   *  COMMISSION INPUT as of CEO 2026-07-25: charge cost now feeds the ALL-IN
   *  commission margin basis on the BOOKED (seed) side, via lineAllInCostCents in
   *  commission-margin.ts. The revenue side (the final invoice total) has always
   *  included the charge SELL, so folding the charge COST into the booked cost is
   *  what makes booked margin symmetric — it can only REDUCE overstatement, never
   *  add it (a charge with no cost on file reads 0; see lineChargesCostCents). It
   *  still does NOT touch order/invoice TOTALS — `amount` (the sell) alone drives
   *  those. */
  cost?: string | number | null;
  /** How many of this charge (Stephanie 2026-10-27). NULL / omitted = the
   *  default: a fixed charge counts once, a per-piece charge counts the line's
   *  units. Set when the rep types a different number (2 setups, a run charge
   *  on only part of the order). */
  quantity?: number | null;
}

/** How many times a charge is billed on its line: the rep's quantity when set,
 *  else fixed → 1 and per_piece → the line's units. ONE rule for every total
 *  (quote, client page, invoice, PO, commission, QuickBooks). */
export function chargeQuantity(c: Pick<MoneyCharge, "billingModel" | "quantity">, lineQuantity: number): number {
  if (c.quantity != null && Number.isInteger(c.quantity) && c.quantity >= 0) return c.quantity;
  return c.billingModel === "per_piece" ? lineQuantity : 1;
}

/** The minimum shape the money math needs off a line. */
export interface MoneyLine {
  unitPrice: string | number;
  quantityTotal: number;
  variants?: readonly MoneyVariant[] | null;
  decorationCharges?: readonly MoneyCharge[] | null;
  /** "shipping" lines are never taxed (Stephanie 2026-10-14: shipping cost is
   *  tax exempt). Omitted = taxed, as before. */
  serviceType?: string | null;
}

export interface OrderTotals {
  /** Exact integer cents — the authoritative values. */
  subtotalCents: number;
  taxCents: number;
  totalCents: number;
  /** Dollar conveniences, re-derived from cents. Never accumulate with these. */
  subtotal: number;
  tax: number;
  total: number;
}

// ─── Line math ────────────────────────────────────────────────────────────

/**
 * True when at least one variant carries an EXPLICIT price — the switch between
 * itemised per-colour math and legacy line-level math. Deliberately
 * conservative: a line whose variants are all unpriced keeps its original total
 * exactly. An explicit ZERO counts as priced (a free giveaway colour must
 * contribute $0, not silently fall back to the line price).
 */
export function hasVariantPricing(line: MoneyLine): boolean {
  const variants = line.variants ?? [];
  return variants.some((v) => v.unitPrice != null && v.unitPrice !== "");
}

/**
 * Goods (product) amount for one line in cents, excluding decoration charges.
 * Variant-priced → Σ quantity × (variant price ?? line price). Otherwise
 * line price × quantityTotal.
 */
export function lineGoodsCents(line: MoneyLine): number {
  if (!hasVariantPricing(line)) {
    return assertSafeCents(
      toCents(line.unitPrice) * line.quantityTotal,
      "line goods amount",
    );
  }
  const linePriceCents = toCents(line.unitPrice);
  let cents = 0;
  for (const v of line.variants ?? []) {
    const priced = v.unitPrice != null && v.unitPrice !== "";
    cents += (priced ? toCents(v.unitPrice) : linePriceCents) * v.quantity;
  }
  return assertSafeCents(cents, "line goods amount");
}

/**
 * Decoration charges for one line in cents. `fixed` is a flat add (a $44 setup
 * fee); `per_piece` multiplies by the line quantity (a $0.40 run charge on 600
 * units = $240) — unless the rep set the charge's own quantity (chargeQuantity).
 */
export function lineChargesCents(line: MoneyLine): number {
  let cents = 0;
  for (const c of line.decorationCharges ?? []) {
    cents += toCents(c.amount) * chargeQuantity(c, line.quantityTotal);
  }
  return assertSafeCents(cents, "line charges amount");
}

/**
 * Decoration-charge COST for one line in cents — the mirror of lineChargesCents
 * on the cost side (CEO 2026-07-24). Same billing scaling: `fixed` is a flat
 * cost, `per_piece` multiplies by the line quantity. A charge with no cost on
 * file contributes 0 (never negative, never NaN). This is a rep-facing
 * economics figure only — it does NOT touch totals and is NOT a commission
 * input.
 */
export function lineChargesCostCents(line: MoneyLine): number {
  return millsToCents(lineChargesCostMills(line));
}

/**
 * lineChargesCostCents at MILL precision — the primitive. A per-piece run charge
 * costing 0.425 on 600 units is 255,000 mills = $255.00 exactly; rounding the
 * 0.425 to cents first would have booked $252.00 or $258.00 depending on the
 * tie-break. Sum here, round once in the caller.
 */
export function lineChargesCostMills(line: MoneyLine): number {
  let mills = 0;
  for (const c of line.decorationCharges ?? []) {
    mills += toMills(c.cost ?? 0) * chargeQuantity(c, line.quantityTotal);
  }
  return assertSafeMills(mills, "line charges cost amount");
}

/** Everything one line contributes, in cents — the per-product "Total" row. */
export function lineTotalCents(line: MoneyLine): number {
  return assertSafeCents(lineGoodsCents(line) + lineChargesCents(line), "line total");
}

/**
 * The line's COST in cents — the mirror of lineGoodsCents.
 *
 * Variant-aware for the same reason prices are: a rep can negotiate a different
 * cost per colour with the supplier. A variant with no explicit cost falls back
 * to the line cost (never free). Zero-drift for existing data: no UI ever wrote
 * `line_item_variants.unit_cost`, so every existing variant takes the fallback
 * and totals the same as `netCost × quantityTotal` did.
 */
export function lineCostCents(
  line: MoneyLine & { netCost?: string | number | null },
): number {
  return millsToCents(lineCostMills(line));
}

/**
 * lineCostCents at MILL precision — the primitive that actually stops the leak.
 *
 * The per-unit cost is extended by quantity BEFORE any rounding, so a supplier's
 * three-decimal net cost survives the multiplication: Digispec BG7 at 1.692 ×
 * 5,000 books 8,460,000 mills = $8,460.00. The old code rounded 1.692 → 169
 * cents first and booked $8,450.00 — $10 short on one line, and the error scales
 * linearly with quantity, which is exactly where it hurts.
 */
export function lineCostMills(
  line: MoneyLine & { netCost?: string | number | null },
): number {
  const lineCostEach = toMills(line.netCost ?? 0);
  const variants = line.variants ?? [];
  const anyVariantCost = variants.some(
    (v) => v.unitCost != null && v.unitCost !== "",
  );
  if (!anyVariantCost) {
    return assertSafeMills(lineCostEach * line.quantityTotal, "line cost");
  }
  let mills = 0;
  for (const v of variants) {
    const priced = v.unitCost != null && v.unitCost !== "";
    mills += (priced ? toMills(v.unitCost) : lineCostEach) * v.quantity;
  }
  return assertSafeMills(mills, "line cost");
}

/**
 * The line's ALL-IN cost in cents — goods cost (variant-aware) PLUS decoration/
 * additional charge cost. The cost-side mirror of lineTotalCents (which is goods
 * sell + charge sell). This is the all-inclusive cost basis the CEO's 2026-07-25
 * commission model requires:
 *   • item cost                      → lineCostCents (variant-aware net cost)
 *   • decoration charges (setup/run) → lineChargesCostCents (charge_type
 *                                      setup/run/screen/digitizing/pms_match)
 *   • additional charges (rush/misc) → lineChargesCostCents (charge_type
 *                                      rush/other), OR a service line's netCost
 *   • shipping / freight             → its OWN line item (serviceType='shipping')
 *                                      carrying a netCost, so its cost enters this
 *                                      sum as that line's goods cost via
 *                                      lineCostCents — NO special case needed.
 *
 * A charge (or a line) with no cost on file contributes 0 (see lineChargesCostCents
 * / lineCostCents): the columns are NOT NULL DEFAULT 0, so "genuinely free" and
 * "not yet costed" are indistinguishable and both read as 0. On the BOOKED
 * (as-sold) basis that is the rep's own assertion — exactly how a line netCost of
 * 0 is already treated — and the BILLED side (actual vendor bills) is the
 * correction path. Using cost=0 can only make the cost side SMALLER, never larger,
 * so this helper can never UNDERSTATE cost beyond what the data supports.
 */
export function lineAllInCostCents(
  line: MoneyLine & { netCost?: string | number | null },
): number {
  // Sum in MILLS and round ONCE. Adding two already-rounded cent figures would
  // reintroduce up to a half-cent of the error this path exists to remove.
  return millsToCents(
    assertSafeMills(
      lineCostMills(line) + lineChargesCostMills(line),
      "line all-in cost",
    ),
  );
}

/**
 * Margin % on the GOODS portion ONLY. Decoration charges are excluded here.
 *
 * HISTORY: this used to note charge cost "isn't tracked per charge." As of
 * 2026-07-24 (CEO) it IS — see MoneyCharge.cost + lineChargesCostCents. As of
 * 2026-07-25 (CEO) the all-inclusive margin (goods + charges + shipping) is the
 * commission basis — but that is computed via lineAllInCostCents, NOT this
 * helper. lineMarginPct's behaviour stays DELIBERATELY GOODS-ONLY: it is the
 * rep-facing "margin on the goods" figure, kept separate so a charge-inclusive
 * definition change never silently moves it. For the all-inclusive rep-facing
 * margin use lineMarginPctWithCharges; for the all-in cost basis use
 * lineAllInCostCents. Null when there's no goods cost on file or nothing sold
 * (never a fake 100%).
 *
 * Convention: MARGIN, not markup — (sell − cost) / sell. This matches
 * `orders.default_margin_pct`, which the add-product path already uses as
 * `unit_price = net_cost / (1 − margin/100)`.
 */
export function lineMarginPct(
  line: MoneyLine & { netCost?: string | number | null },
): number | null {
  const goods = lineGoodsCents(line);
  if (goods <= 0) return null;
  const costCents = lineCostCents(line);
  if (costCents <= 0) return null;
  return ((goods - costCents) / goods) * 100;
}

/**
 * Charge-inclusive margin % — goods AND decoration charges on both sides
 * (CEO 2026-07-24: "a margin column so the rep can see what margin they're
 * charging"). Sell = goods sell + charge sell (amount); cost = goods cost +
 * charge cost. Null when there's nothing sold or no cost basis at all.
 *
 * REP-FACING ECONOMICS DISPLAY ONLY. This is a NEW helper alongside
 * lineMarginPct precisely so the latter's commission consumers don't move.
 * It is NOT wired into commissions.
 */
export function lineMarginPctWithCharges(
  line: MoneyLine & { netCost?: string | number | null },
): number | null {
  const sell = lineGoodsCents(line) + lineChargesCents(line);
  if (sell <= 0) return null;
  const cost = lineCostCents(line) + lineChargesCostCents(line);
  if (cost <= 0) return null;
  return ((sell - cost) / sell) * 100;
}

// ─── Margin ⇄ price (exact, integer-only) ─────────────────────────────────

/** Margin percent → integer HUNDREDTHS of a percent (44.34% → 4434). */
export function marginHundredths(v: string | number | null | undefined): number | null {
  if (v == null || v === "") return null;
  const n = typeof v === "number" ? v : Number(String(v).trim());
  if (!Number.isFinite(n)) return null;
  return Math.round(n * 100);
}

/**
 * A margin the system can actually price from. 100% or more is impossible
 * (sell would be infinite); negative is allowed — selling below cost is a real,
 * deliberate thing a rep sometimes does and the document must be able to say so.
 */
export function isPriceableMargin(marginH: number | null): marginH is number {
  return marginH != null && Number.isFinite(marginH) && marginH < 10_000;
}

/**
 * SELL price from COST and a target margin, exactly, with no floating point:
 *
 *     sell = cost / (1 − m)          where m = marginH / 10000
 *          = cost × 10000 / (10000 − marginH)
 *
 * Half-up on the final division only. Returns null for an impossible margin
 * (≥ 100%) so the caller can refuse rather than emit a nonsense price.
 */
export function sellCentsFromCostAndMargin(
  costCents: number,
  marginH: number,
): number | null {
  if (!isPriceableMargin(marginH)) return null;
  const denominator = 10_000 - marginH;
  if (denominator <= 0) return null;
  const numerator = assertSafeCents(costCents * 10_000, "margin price numerator");
  return divideRoundHalfUp(numerator, denominator);
}

/** Two decimal places, e.g. "44.34%". Null → em dash. */
export function formatPct(pct: number | null): string {
  return pct == null ? "—" : `${pct.toFixed(2)}%`;
}

// ─── Document totals ──────────────────────────────────────────────────────

/**
 * Document totals. `taxRatePct` is a 0..100 PERCENT (orders.default_tax_rate);
 * null/0/negative/garbage means no tax — tax is never a credit.
 *
 * Tax uses NO floating point: exact integer cents × exact integer rate
 * (hundredths of a percent) ÷ 10,000, half-up. This deliberately does NOT
 * reproduce the pre-2026-07-19 float formula, which was wrong on boundary cases
 * — e.g. $1.16 @ 12.50% is exactly $0.145 and must round to $0.15, but
 * `1.16 * 12.5` evaluates to 14.499999999999998 and produced $0.14.
 */
export function computeOrderTotals(
  lines: readonly MoneyLine[],
  taxRatePct: string | number | null | undefined,
): OrderTotals {
  let subtotalCents = 0;
  // Tax is on the items only — shipping is tax exempt (Stephanie 2026-10-14).
  let taxableCents = 0;
  for (const line of lines) {
    const cents = lineTotalCents(line);
    subtotalCents += cents;
    if (line.serviceType !== "shipping") taxableCents += cents;
  }
  assertSafeCents(subtotalCents, "subtotal");

  const rateH = rateHundredths(taxRatePct);
  const taxCents =
    rateH > 0 && taxableCents > 0
      ? divideRoundHalfUp(assertSafeCents(taxableCents * rateH, "tax numerator"), 10_000)
      : 0;
  const totalCents = assertSafeCents(subtotalCents + taxCents, "total");

  return {
    subtotalCents,
    taxCents,
    totalCents,
    subtotal: subtotalCents / 100,
    tax: taxCents / 100,
    total: totalCents / 100,
  };
}

/**
 * The wire shape the document routes return (2dp strings), plus the line count.
 * Rendered straight from integer cents — no float ever touches the output.
 */
export function formatOrderTotals(
  totals: OrderTotals,
  lineCount: number,
): { subtotal: string; tax: string; total: string; lineCount: number } {
  return {
    subtotal: centsToString(totals.subtotalCents),
    tax: centsToString(totals.taxCents),
    total: centsToString(totals.totalCents),
    lineCount,
  };
}

// ─── Margin reprice PLAN (pure, testable) ─────────────────────────────────

/** One price write a margin edit wants to make. */
export interface MarginWrite {
  /** null = the line's own fallback price; otherwise the variant's index. */
  variantIndex: number | null;
  /** 2dp string, ready for the API. */
  unitPrice: string;
}

/**
 * Is there ANY cost on this line to derive a margin from — either the line's
 * own, or one of its variants'?
 *
 * Gates whether a margin EDIT is offered at all. It must NEVER be used to
 * authorise an individual write: that is what broke three times (a line with no
 * cost of its own but a costed variant passed this check and then had its
 * fallback price written to $0.00, zeroing every un-priced colour). Individual
 * writes re-check their own basis inside marginRepriceWrites.
 *
 * Lives here, beside the writer, because review flagged the component holding a
 * second independent copy of this rule — the same duplicate-rule drift that
 * caused every other defect in this feature.
 */
export function hasUsableCostBasis(
  line: MoneyLine & { netCost?: string | number | null },
): boolean {
  if (toCents(line.netCost ?? 0) !== 0) return true;
  return (line.variants ?? []).some((v) => toCents(v.unitCost) !== 0);
}

/**
 * ONE row's sell price from its cost (in MILLS) and a target margin
 * (hundredths of a percent). The exact rule marginRepriceWrites applies to each
 * row — extracted unchanged so a single row (one colour/size, a breakdown row)
 * can be priced from its own margin box (Stephanie 2026-10-14) without a second
 * copy of the math. Null = no cost basis or an impossible margin: never write.
 */
export function sellPriceFromCostMills(costMills: number, marginH: number): string | null {
  if (costMills === 0) return null; // no basis → no write, ever
  if (!isPriceableMargin(marginH)) return null;
  const denominator = 10_000 - marginH;
  if (denominator <= 0) return null;
  // 🔒 ONE ROUNDING, NOT TWO. An earlier revision divided to mills and then
  // divided again to cents. Double rounding is NOT the same as rounding once:
  // swept across whole-percent margins on plain two-decimal costs, 4.4% of
  // pairs came out a cent away from what the same reprice produced before this
  // branch — e.g. 4.98 @ 22% gave 6.39 where it had always given 6.38. That
  // silently contradicted the safety claim this whole change rests on.
  //
  // sell_cents = cost_mills × 1000 / (10000 − marginH), in a single half-up
  // divide. For a TWO-decimal cost, cost_mills is exactly cost_cents × 10, so
  // this reduces algebraically to the original cost_cents × 10000 / denominator
  // — the pre-existing formula, bit-for-bit. The invariant is now structural
  // rather than something that happened to hold on the samples we picked.
  return centsToString(
    divideRoundHalfUp(
      assertSafeMills(costMills * 1_000, "margin price numerator"),
      denominator,
    ),
  );
}

/**
 * Retail UNLOCKED (Stephanie 2026-10-14, commonsku's lock): when a row's cost
 * changes, keep the margin it had and re-price from the new cost. Returns the
 * new sell price, or null when there was no margin to keep (no price or no
 * cost before) or no new cost — then only the cost changes.
 */
export function keepMarginSellPrice(
  oldCostMills: number,
  oldPriceCents: number,
  newCostMills: number,
): string | null {
  if (oldCostMills <= 0 || oldPriceCents <= 0 || newCostMills <= 0) return null;
  const sellMills = oldPriceCents * 10;
  // Margin in hundredths of a percent, rounded once — the same precision a rep
  // can type into a margin box.
  const marginH = divideRoundHalfUp((sellMills - oldCostMills) * 10_000, sellMills);
  return sellPriceFromCostMills(newCostMills, marginH);
}

/**
 * Decide EXACTLY which prices a margin edit should write. Pure, so the offline
 * gate can execute it — which is the point.
 *
 * This logic lived inside a React component and broke three times in three
 * review iterations, each time in a way no source-lint could see: it wrote a
 * price with no cost behind it. It is extracted here so the rule can be tested
 * against real inputs rather than pattern-matched.
 *
 * THE INVARIANT: every write is derived from a cost that actually exists on the
 * thing being priced. Never invent a price.
 *
 * The trap it keeps falling into: `line.unitPrice` is a FALLBACK — any variant
 * without its own price inherits it (see lineGoodsCents). So a line price
 * derived from a ZERO line-cost silently drops every un-priced colour to $0.00,
 * including colours the rep never touched. A line-level "does any cost exist?"
 * check is therefore NOT sufficient to authorise a line-level write.
 */
export function marginRepriceWrites(
  line: MoneyLine & { netCost?: string | number | null },
  marginH: number,
): MarginWrite[] {
  if (!isPriceableMargin(marginH)) return [];
  const writes: MarginWrite[] = [];

  // ⚠️ THE ONE PLACE A COST'S THIRD DECIMAL CAN MOVE A SELL PRICE — by design.
  // This function answers a rep's explicit request: "price this line at 33%."
  // Deriving that price from a cost we had just rounded away from would hand
  // back a price that does NOT achieve the margin they asked for — cost 1.694 @
  // 33% yields 2.52 from the rounded cost and 2.53 from the true one. The output
  // is still a 2dp SELL string; only the basis got more accurate.
  //
  // 🔴 THE REAL BOUND, corrected after review (2026-08-11). An earlier comment
  // here claimed "at most ONE CENT". That is FALSE, and the error grows without
  // limit as the margin approaches 100%, because sell = cost / (1 − margin) and
  // the divisor `10000 − marginH` shrinks toward 1:
  //
  //     cost 1.699 @ 50%    → no change
  //     cost 1.699 @ 86%    → no change
  //     cost 1.699 @ 90%    → 1 cent
  //     cost 1.699 @ 99%    → 10 cents
  //     cost 1.699 @ 99.99% → $10.00
  //
  // ≤1 cent holds only up to roughly 86% margin. `isPriceableMargin` permits
  // anything under 100%, so the extreme cases are reachable — they are absurd
  // for this business, but the code must not claim a guarantee it does not have.
  //
  // ✅ WHAT IS GUARANTEED, and it is the part that matters: for a TWO-decimal
  // cost the result is bit-identical to the pre-existing formula at EVERY
  // margin — not empirically, but algebraically, because cost_mills is exactly
  // cost_cents × 10 and the single division reduces to the old one. Every cost
  // in production today is two-decimal, so NO existing quote moves.
  //
  // ⚠️ An earlier version of this comment made the same claim while the code
  // rounded twice, and it was FALSE on 4.4% of ordinary inputs. The gate now
  // SWEEPS every whole-percent margin rather than sampling — a claim about all
  // margins cannot be evidenced by nine of them.
  const sellFrom = (costMills: number): string | null =>
    sellPriceFromCostMills(costMills, marginH);

  // 1. The line's own fallback price — ONLY when the line itself has a cost.
  const lineCostEach = toMills(line.netCost ?? 0);
  const lineSell = sellFrom(lineCostEach);
  if (lineSell != null) writes.push({ variantIndex: null, unitPrice: lineSell });

  // 2. Each variant, from ITS OWN cost where it has one.
  const variants = line.variants ?? [];
  variants.forEach((v, i) => {
    const ownCost =
      v.unitCost != null && v.unitCost !== "" ? toMills(v.unitCost) : null;
    if (ownCost !== null) {
      const sell = sellFrom(ownCost);
      if (sell != null) writes.push({ variantIndex: i, unitPrice: sell });
      return;
    }
    // No cost of its own. An explicit PRICE wouldn't be moved by the line
    // reprice, so bring it onto the line's basis — but only if the line has one.
    const hasOwnPrice = v.unitPrice != null && v.unitPrice !== "";
    if (hasOwnPrice && lineSell != null) {
      writes.push({ variantIndex: i, unitPrice: lineSell });
    }
    // Otherwise: no basis, or it inherits the repriced line. Leave it alone.
  });

  return writes;
}
